import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import CustomTextField from '../../../../components/CustomTextField/CustomTextField';
import { IUser } from 'types';
import { useAppData } from 'context/AppContext';
import swal from 'sweetalert';

const commonStyles = {
  bgcolor: 'background.paper',
  borderColor: '#9B9B9B',
  m: 1,
  border: 1,
  width: '1rem',
  height: '1rem',
};

type PersonalInfoStandardProps = {
  user: IUser | null;
};

type SelectedValueType = "nameAndPronouns" | "emailPassword";


const PersonalInfoStandard = (props: PersonalInfoStandardProps) => {
  const { user } = props;

  const { updateUserById } = useAppData();

  const [selectedValue, setSelectedValue] =
    useState<SelectedValueType>("nameAndPronouns");

  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');
  const [pronounsValue, setPronounsValue] = useState('');
  const [currentStep2, setCurrentStep2] = useState(0);
  const allSteps: SelectedValueType[] = ["nameAndPronouns", "emailPassword"];
  const [showIcons, setShowIcons] = useState({
    nameAndPronouns: false,
    emailPassword: false,
  });

  const skipFirstTwoSteps = true;
  const steps = skipFirstTwoSteps ? allSteps.slice(0) : allSteps;


  const [emailValue, setEmailValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');

  useEffect(() => {
    if (user) {
      setFirstNameValue(user.firstName);
      setLastNameValue(user.lastName);
      setPronounsValue(user.pronouns);

      setEmailValue(user.email);
    }
  }, [user]);
  const updateShowIcons = (data: IUser) => {
    setShowIcons({
      nameAndPronouns: !!(data.firstName || data.lastName || data.pronouns || data.profilePicture),
      emailPassword: !!data.email
    });
  };

  const handleSelectionChange = (value: SelectedValueType) => {
    const stepIndex = allSteps.indexOf(value);
    if (stepIndex !== -1) {
      setCurrentStep2(stepIndex);
      setSelectedValue(value);
    }

    setShowIcons((prevState: any) => ({
      ...prevState,
      agreeToTerms: false,
    }));

  };

  const handleSaveChanges = () => {
    if (user) {
      updateUserById(user.id, user)
        .then((response: any) => {
          if (response.success) {
            swal('Success', 'User updated successfully!', 'success');
          } else {
            swal(
              'Error',
              `Failed to update user: ${response.message}`,
              'error'
            );
          }
        })
        .catch((error: any) => {
          swal('Error', `Error updating user: ${error.message}`, 'error');
        });
    }
  };

  const handleNextClick2 = () => {



    if (currentStep2 < steps.length - 1) {
      setCurrentStep2(currentStep2 + 1);
      setSelectedValue(steps[currentStep2 + 1]);
    }
  };

  return (
    <>
      <Box display='flex' minHeight='100vh'>
        <Box width='22%' p={2}>
          <Typography
            variant='h6'
            gutterBottom
            fontSize={18}
            fontWeight={700}
            color={'#595959'}>
            User Account
          </Typography>

          <Box
            display={'flex'}
            gap={2}
            alignItems={'center'}
            position={'relative'}
            onClick={() => handleSelectionChange('nameAndPronouns')}
            sx={{ cursor: 'pointer' }}>
            {selectedValue === 'nameAndPronouns' && (
              <Typography
                sx={{
                  borderLeft: '9px solid #233F91',
                  height: '29px',
                  position: 'absolute',
                  left: '-15px',
                }}></Typography>
            )}
            <Box display={'flex'} gap={1} alignItems={'center'}>
              <Box
                sx={{ ...commonStyles, borderRadius: '50%', color: '#9B9B9B' }}
              />
              <Typography
                sx={{
                  fontSize: '17px',
                  fontWeight: 500,
                  color:
                    selectedValue === 'nameAndPronouns' ? '#233F91' : 'inherit',
                }}>
                Name and Pronoun
              </Typography>
            </Box>
          </Box>

          <Typography
            variant='h6'
            gutterBottom
            mt={2}
            fontSize={18}
            fontWeight={700}
            color={'#595959'}>
            Security Settings
          </Typography>
          <Box
            display={'flex'}
            gap={2}
            alignItems={'center'}
            position={'relative'}
            sx={{ cursor: 'pointer' }}
            onClick={() => handleSelectionChange('emailPassword')}>
            {selectedValue === 'emailPassword' && (
              <Box
                sx={{
                  borderLeft: '9px solid #233F91',
                  height: '29px',
                  position: 'absolute',
                  left: '-15px',
                }}></Box>
            )}
            <Box display={'flex'} gap={1} alignItems={'center'}>
              <Box
                sx={{ ...commonStyles, borderRadius: '50%', color: '#9B9B9B' }}
              />
              <Typography
                sx={{
                  fontSize: '17px',
                  fontWeight: 500,
                  color:
                    selectedValue === 'emailPassword' ? '#233F91' : 'inherit',
                }}>
                Email Address & Password
              </Typography>
            </Box>
          </Box>

          <Box
            display={'flex'}
            justifyContent={'flex-end'}
            height={'60%'}
            flexDirection={'column'}>
            <Button
              onClick={handleSaveChanges}
              sx={{
                width: '214px',
                height: '48px',
                p: '6px 20px',
                backgroundColor: '#233F91',
                borderRadius: '15px',
                color: 'white',
              }}
              type='submit'>
              Save Changes
            </Button>
          </Box>
        </Box>

        <Box
          width='80%'
          bgcolor={'#F8F8F8'}
          sx={{ borderRadius: '10px', border: '1px solid #A9A9A9' }}>
          <Typography
            variant='h5'
            gutterBottom
            fontSize={18}
            p={2}
            fontWeight={700}
            color={'#797979'}>
            {selectedValue === 'nameAndPronouns'
              ? 'Name and Pronouns'
              : 'Email Address & Password'}
          </Typography>
          <Typography
            sx={{
              borderBottom: '2px solid #A9A9A9',
              width: '100%',
              mt: 1,
            }}></Typography>

          {selectedValue === 'nameAndPronouns' ? (
            <Grid display={'flex'} gap={5} flexDirection={'column'} p={5}>
              <Grid
                item
                xs={12}
                sm={6}
                display={'flex'}
                flexDirection={'column'}>
                <label
                  htmlFor='name'
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#000000',
                    marginLeft: '10px',
                  }}>
                  First Name
                </label>
                <Grid display={'flex'} gap={2} alignItems={'center'} flex={1}>
                  <Box sx={{ flexGrow: 1 }}>
                    <CustomTextField
                      name='name'
                      sx={{ width: '100%', maxWidth: '654px' }}
                      value={user?.firstName || ''}
                      onChange={function (
                        e: React.ChangeEvent<HTMLInputElement>
                      ): void {
                        setFirstNameValue(e.target.value);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                display={'flex'}
                flexDirection={'column'}>
                <label
                  htmlFor='name'
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#000000',
                    marginLeft: '10px',
                  }}>
                  Last Name
                </label>
                <Grid display={'flex'} gap={2} alignItems={'center'} flex={1}>
                  <Box sx={{ flexGrow: 1 }}>
                    <CustomTextField
                      name='name'
                      sx={{ width: '100%', maxWidth: '654px' }}
                      value={user?.lastName || ''}
                      onChange={function (
                        e: React.ChangeEvent<HTMLInputElement>
                      ): void {
                        setLastNameValue(e.target.value);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                display={'flex'}
                flexDirection={'column'}>
                <label
                  htmlFor='pronouns'
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#000000',
                    marginLeft: '10px',
                  }}>
                  Pronouns
                </label>
                <Box sx={{ flexGrow: 1 }}>
                  <CustomTextField
                    name='pronouns'
                    sx={{ width: '100%', maxWidth: '654px' }}
                    value={user?.pronouns || ''}
                    onChange={function (
                      e: React.ChangeEvent<HTMLInputElement>
                    ): void {
                      setPronounsValue(e.target.value);
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Grid display={'flex'} gap={5} flexDirection={'column'} p={5}>
              <Grid
                item
                xs={12}
                sm={6}
                display={'flex'}
                flexDirection={'column'}>
                <label
                  htmlFor='email'
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#000000',
                    marginLeft: '10px',
                  }}>
                  Email Address
                </label>
                <Grid display={'flex'} gap={2} alignItems={'center'} flex={1}>
                  <Box sx={{ flexGrow: 1 }}>
                    <CustomTextField
                      name='email'
                      sx={{ width: '100%', maxWidth: '654px' }}
                      value={user?.email || ''}
                      onChange={function (
                        e: React.ChangeEvent<HTMLInputElement>
                      ): void {
                        setEmailValue(e.target.value);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              {/* <Grid
                item
                xs={12}
                sm={6}
                display={'flex'}
                flexDirection={'column'}>
                <label
                  htmlFor='password'
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#000000',
                    marginLeft: '10px',
                  }}>
                  Password
                </label>
                <Grid display={'flex'} gap={2} alignItems={'center'} flex={1}>
                  <Box sx={{ flexGrow: 1 }}>
                    <CustomTextField
                      name='password'
                      type='password'
                      sx={{ width: '100%', maxWidth: '654px' }}
                      value={passwordValue}
                      onChange={function (
                        e: React.ChangeEvent<HTMLInputElement>
                      ): void {
                        setPasswordValue(e.target.value);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid> */}
            </Grid>
          )}

          <Box
            mt={3}
            display='flex'
            alignItems={'center'}
            justifyContent='center'
            height={'40%'}>
            <Button
              onClick={handleNextClick2}
              // disabled={!showIcons[selectedValue]}
              sx={{
                color: '#595959',
                bgcolor: '#DBDBDB',
                borderRadius: '10px',
                border: '1px solid #B3B3B3',
                fontWeight: 400,
                fontSize: '15px',
              }}>
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PersonalInfoStandard;
